import React, { useState, useCallback, useEffect } from 'react';
import { getItem } from '@/shared/utils/storage';
import { saveFilters } from '@/shared/utils/filterStorage';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { DeliveryHistoryDetailFilters } from '../types/deliveryHistoryDetail.types';
import { deliveryHistoryDetailApi } from '../api/deliveryHistoryDetailApi';
import { useDeliveryDetailFilter, FILTER_STORAGE_KEY } from './useDeliveryDetailFilter';
import { useDeliveryDetailOptions } from './useDeliveryDetailOptions';
import { useDeliveryDetailData } from './useDeliveryDetailData';
import { getDefaultFilters } from '../utils/deliveryHistoryDetailHelpers';

const SESSION_PREFILL_KEY = 'deliveryHistoryDetailPrefill';
const SESSION_SOURCE_KEY = 'deliveryHistoryDetailPrefillSource';

export const useDeliveryHistoryDetail = () => {
    const userType = getItem<string>('USER_TYPE') || '';
    const userId = getItem<string>('USER_ID') || '';
    const isSupplierUser = userType === 'SUPPLIER' && !!userId;
    const { showToast } = useToast();

    const { filters, setFilters, handleFilterChange, resetFilters } = useDeliveryDetailFilter();
    const { suppliers, suppliersLoading, riders, ridersLoading } = useDeliveryDetailOptions({
        isSupplierUser,
        userId,
        filters,
        setFilters,
    });
    const { results, riderSummaries, summaryInfo, isSummaryView, loading, error, contextInfo, fetchDetails } =
        useDeliveryDetailData(filters);

    const [excelDownloading, setExcelDownloading] = useState(false);

    useEffect(() => {
        const source = sessionStorage.getItem(SESSION_SOURCE_KEY);
        const stored = sessionStorage.getItem(SESSION_PREFILL_KEY);

        if (source === 'history' && stored) {
            try {
                const parsed = JSON.parse(stored) as Partial<DeliveryHistoryDetailFilters>;
                const merged: DeliveryHistoryDetailFilters = { ...getDefaultFilters(), ...parsed };
                setFilters(merged);
                fetchDetails(merged);
            } catch {
                // prefill 파싱 실패 시 무시
            }
        } else if (filters.suppId) {
            fetchDetails();
        }

        sessionStorage.removeItem(SESSION_PREFILL_KEY);
        sessionStorage.removeItem(SESSION_SOURCE_KEY);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!filters.suppId) {
                showToast('협력사를 선택 후 검색 가능합니다.', { type: ToastType.WARNING });
                return;
            }
            fetchDetails();
            saveFilters(FILTER_STORAGE_KEY, filters);
        },
        [fetchDetails, filters, showToast]
    );

    const handleExcelDownload = useCallback(async () => {
        if (!filters.startDate || !filters.endDate) {
            showToast('조회일자를 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        setExcelDownloading(true);
        try {
            const blob = await deliveryHistoryDetailApi.downloadExcel(filters);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            const startDate = filters.startDate.replace(/-/g, '');
            const endDate = filters.endDate.replace(/-/g, '');
            link.download = `${startDate}~${endDate}_배달상세내역.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setExcelDownloading(false);
        }
    }, [filters, showToast]);

    return {
        filters,
        results,
        riderSummaries,
        summaryInfo,
        isSummaryView,
        loading,
        error,
        handleFilterChange,
        handleSubmit,
        resetFilters,
        refetch: fetchDetails,
        contextInfo,
        suppliers,
        suppliersLoading,
        riders,
        ridersLoading,
        isSupplierUser,
        excelDownloading,
        handleExcelDownload,
    };
};
